import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";

export interface SidebarItem {
  title: string;
  url: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>> | string;
  disabled: boolean;
  isActive?: boolean;
  children?: SidebarItem[];
}

function normalizePath(path: string): string {
  return path.replace(/[/#]+$/, "");
}

export function useSidebarActiveItems(
  initialItems: SidebarItem[],
  baseRoute: string = "/visits",
): SidebarItem[] {
  const location = useLocation();
  const currentPath = location.pathname;

  const normalizedCurrentPath = normalizePath(currentPath);

  const updatedItems = useMemo(() => {
    const updateItems = (items: SidebarItem[]): SidebarItem[] =>
      items.map((item) => {
        const resolvedUrl = item.url === "#" ? baseRoute : item.url;
        const normalizedItemUrl = normalizePath(resolvedUrl);

        let isActive =
          normalizedCurrentPath === normalizedItemUrl ||
          normalizedCurrentPath.startsWith(`${normalizedItemUrl}/`);

        if (item.children) {
          const updatedChildren = updateItems(item.children);
          if (updatedChildren.some((child) => child.isActive)) {
            isActive = true;
          }
          return { ...item, isActive, children: updatedChildren };
        }

        return { ...item, isActive };
      });

    return updateItems(initialItems);
  }, [initialItems, normalizedCurrentPath, baseRoute]);

  return updatedItems;
}
